import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { changeNewPasswordRequest } from "../../../../../Redux/Slices/Common/commonSlice";

// Custom components
import Button from "Components/Button";
import ErrorMessage from "Components/ErrorMessage";
import InputBox from "Components/InputBox";
import { notifySuccess } from "Services/Utils/toastNotification";

const ChangePassword = ({ goToNextTab }) => {
  const dispatch = useDispatch();

  const {
    handleSubmit,
    control,
    watch,
    setError,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const currentPassword = watch("currentPassword");
  const newPassword = watch("newPassword");

  const handlePasswordValidation = (field, value) => {
    if (field === "newPassword") {
      if (value === currentPassword) {
        setError("newPassword", {
          type: "manual",
          message: "New password must be not be same as Current password.",
        });
      } else if (value === "") {
        setError("newPassword", {
          type: "manual",
          message: "New password is required.",
        });
      } else {
        clearErrors("newPassword");
      }
    }

    if (field === "confirmPassword") {
      if (value !== newPassword) {
        setError("confirmPassword", {
          type: "manual",
          message: "Confirm password must match with New Password.",
        });
      } else if (value === "") {
        setError("confirmPassword", {
          type: "manual",
          message: "Confirm New password is required.",
        });
      } else {
        clearErrors("confirmPassword");
      }
    }
  };

  const onSubmit = (data) => {
    dispatch(
      changeNewPasswordRequest({
        data: {
          new_password: data.newPassword,
          confirm_password: data.confirmPassword,
          password: data.currentPassword,
        },
        callBack: () => {
          notifySuccess("Password changed successfully!");
          goToNextTab();
        },
      })
    );
  };

  const buttonTitleStyle = {
    fontSize: "16px",
    fontWeight: "600",
  };

  const buttonStyle = {
    height: "44px",
    width: "170px",
    whiteSpace: "nowrap",
    borderRadius: "12px",
  };

  const settingBoxStyle = {
    border: "1px solid #858485",
    outline: "none",
    borderRadius: "12px",
  };

  const settingLabelStyle = {
    color: "#545454",
    fontSize: "16px",
    fontWeight: "400",
  };

  useEffect(() => {
    if (!currentPassword) {
      setValue("newPassword", "");
      setValue("confirmPassword", "");
      clearErrors(["newPassword", "confirmPassword"]);
    }
  }, [currentPassword, setValue, clearErrors]);

  return (
    <div className="p-4">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="col-6 mb-5 mt-2">
            <Controller
              name="currentPassword"
              control={control}
              rules={{
                required: "Current password is required.",
              }}
              render={({ field }) => (
                <InputBox
                  {...field}
                  label="Current Password"
                  type="password"
                  inputStyle={settingBoxStyle}
                  labelStyle={settingLabelStyle}
                />
              )}
            />
            <div className="pt-1">
              <ErrorMessage message={errors.currentPassword?.message} />
            </div>
          </div>
          <div className="col-6 mb-5 mt-2">
            <Controller
              name="newPassword"
              control={control}
              rules={{
                required: currentPassword ? "New password is required." : false,
                minLength: {
                  value: 8,
                  message: "Password must be at least 8 characters.",
                },
                validate: (value) => {
                  if (!currentPassword) return true;
                  return value !== currentPassword ||
                    "New password must not be the same as Current password.";
                }
              }}
              render={({ field }) => (
                <InputBox
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handlePasswordValidation("newPassword", e.target.value);
                  }}
                  label="New Password"
                  type="password"
                  inputStyle={settingBoxStyle}
                  labelStyle={settingLabelStyle}
                  disabled={!currentPassword}
                  name="newPassword"
                />
              )}
            />
            <div className="pt-1">
              <ErrorMessage message={errors.newPassword?.message} />
            </div>
          </div>

          {/* Confirm New Password */}
          <div className="col-6">
            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: currentPassword && newPassword ? "Confirm new password is required." : false,
                validate: (value) => {
                  if (!currentPassword || !newPassword) return true;
                  return value === newPassword || "Passwords must match.";
                }   
              }}
              render={({ field }) => (
                <InputBox
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    handlePasswordValidation("confirmPassword", e.target.value);
                  }}
                  label="Confirm New Password"
                  type="password"
                  inputStyle={settingBoxStyle}
                  labelStyle={settingLabelStyle}
                  disabled={!currentPassword || !newPassword}
                />
              )}
            />
            <div className="pt-1">
              <ErrorMessage message={errors.confirmPassword?.message} />
            </div>
          </div>
        </div>

        <div className="col-2 mt-5">
          <Button
            title="Submit & Next"
            titleStyle={buttonTitleStyle}
            buttonStyle={buttonStyle}
            forForm
          />
        </div>
      </form>
    </div>
  );
};

export default ChangePassword;
